import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import '../hd-styles.css';

function Rewards() {
  const navigate = useNavigate();
  const [rewards, setRewards] = useState(null);
  const [allRewards, setAllRewards] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeCosmetics, setActiveCosmetics] = useState({
    frame: null,
    title: 'Newcomer',
    theme: null,
    effect: null
  });
  const [activeTab, setActiveTab] = useState('cosmetics');

  const accountHash = localStorage.getItem('avalw_account_hash');

  useEffect(() => {
    if (!accountHash) {
      navigate('/');
      return;
    }
    fetchRewards();
  }, []);

  const fetchRewards = async () => {
    try {
      console.log('[Rewards] Fetching for account:', accountHash);
      const response = await fetch(`/api/rewards/${accountHash}`);
      const data = await response.json();
      console.log('[Rewards] API response:', data);

      if (data.success) {
        setRewards(data);
        console.log('[Rewards] CO2:', data.co2, 'Level:', data.level);
        setAllRewards(data.all_rewards || {});
        setActiveCosmetics(data.rewards?.active || {
          frame: null,
          title: 'Newcomer',
          theme: null,
          effect: null
        });
      }
    } catch (err) {
      console.error('Failed to fetch rewards');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`/api/rewards/${accountHash}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          active_frame: activeCosmetics.frame,
          active_title: activeCosmetics.title,
          active_theme: activeCosmetics.theme,
          active_effect: activeCosmetics.effect
        })
      });

      const data = await response.json();
      if (data.success) {
        setActiveCosmetics(data.active);
      }
    } catch (err) {
      console.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const isUnlocked = (type, value) => {
    if (!rewards) return false;
    return rewards.rewards?.unlocked?.[type + 's']?.includes(value);
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const frameRewards = [
    { value: null, name: 'None', level: 1, color: '#64748b' },
    { value: 'bronze', name: 'Bronze', level: 3, color: '#d97706' },
    { value: 'silver', name: 'Silver', level: 10, color: '#94a3b8' },
    { value: 'gold', name: 'Gold', level: 20, color: '#fbbf24' },
    { value: 'platinum', name: 'Platinum', level: 30, color: '#06b6d4' },
    { value: 'legendary', name: 'Legendary', level: 50, color: '#a855f7' }
  ];

  const titleRewards = [
    { value: 'Newcomer', name: 'Newcomer', level: 1 },
    { value: 'Contributor', name: 'Contributor', level: 5 },
    { value: 'Eco Warrior', name: 'Eco Warrior', level: 12 },
    { value: 'Cache Master', name: 'Cache Master', level: 25 },
    { value: 'Elite Guardian', name: 'Elite Guardian', level: 40 },
    { value: 'Planet Saver', name: 'Planet Saver', level: 50 }
  ];

  const themeRewards = [
    { value: null, name: 'Default', level: 1, colors: ['#f8fafc', '#e2e8f0'] },
    { value: 'mint', name: 'Mint Fresh', level: 8, colors: ['#d1fae5', '#6ee7b7'] },
    { value: 'sunset', name: 'Sunset Glow', level: 18, colors: ['#fef3c7', '#fbbf24'] },
    { value: 'nebula', name: 'Cosmic Nebula', level: 45, colors: ['#ede9fe', '#a78bfa'] }
  ];

  const effectRewards = [
    { value: null, name: 'None', level: 1 },
    { value: 'glow', name: 'Soft Glow', level: 15 },
    { value: 'animated', name: 'Pulse Effect', level: 35 }
  ];

  // Calculate stats
  const currentLevel = rewards?.level?.current || 1;
  const levelProgress = rewards?.level?.progress || 0;
  const co2Saved = rewards?.co2?.kg || rewards?.level?.co2_kg || 0;
  const coreHours = rewards?.level?.core_hours || 0;
  const totalUnlocked = (rewards?.rewards?.unlocked?.frames?.length || 0) +
    (rewards?.rewards?.unlocked?.titles?.length || 0) +
    (rewards?.rewards?.unlocked?.themes?.length || 0) +
    (rewards?.rewards?.unlocked?.effects?.length || 0);
  const totalRewards = frameRewards.length + titleRewards.length + themeRewards.length + effectRewards.length;

  if (loading) {
    return (
      <AppLayout>
        <div className="page-loading">
          <div className="spinner"></div>
          <p>Loading rewards...</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="page-content rewards-page-new">
        {/* Hero Header */}
        <div className="rewards-hero">
          <div className="rewards-hero-bg">
            <div className="hero-orb hero-orb-1"></div>
            <div className="hero-orb hero-orb-2"></div>
            <div className="hero-orb hero-orb-3"></div>
          </div>

          <div className="rewards-hero-content">
            <div className="hero-level-ring">
              <svg viewBox="0 0 120 120" className="level-ring-svg">
                <defs>
                  <linearGradient id="levelGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#10b981" />
                    <stop offset="100%" stopColor="#06b6d4" />
                  </linearGradient>
                </defs>
                <circle cx="60" cy="60" r="54" fill="none" stroke="#e2e8f0" strokeWidth="8" />
                <circle
                  cx="60" cy="60" r="54"
                  fill="none"
                  stroke="url(#levelGrad)"
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${levelProgress * 3.39} 339`}
                  style={{ transform: 'rotate(-90deg)', transformOrigin: '50% 50%' }}
                />
              </svg>
              <div className="level-ring-center">
                <span className="level-num">{currentLevel}</span>
                <span className="level-text">LEVEL</span>
              </div>
            </div>

            <div className="hero-info">
              <h1>Contributor Rewards</h1>
              <p className="hero-subtitle">Unlock exclusive cosmetics as you level up</p>

              <div className="hero-stats">
                <div className="hero-stat">
                  <span className="hero-stat-value">{co2Saved.toFixed(1)}</span>
                  <span className="hero-stat-label">kg CO2 Saved</span>
                </div>
                <div className="hero-stat">
                  <span className="hero-stat-value">{formatNumber(coreHours)}</span>
                  <span className="hero-stat-label">Core Hours</span>
                </div>
                <div className="hero-stat">
                  <span className="hero-stat-value">{totalUnlocked}/{totalRewards}</span>
                  <span className="hero-stat-label">Unlocked</span>
                </div>
              </div>
            </div>
          </div>

          <div className="xp-bar-container">
            <div className="xp-bar">
              <div className="xp-fill" style={{ width: `${levelProgress}%` }}></div>
            </div>
            <div className="xp-labels">
              <span>Level {currentLevel}</span>
              <span>{levelProgress.toFixed(0)}%</span>
              <span>Level {currentLevel + 1}</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="rewards-tabs">
          <button
            className={`rewards-tab ${activeTab === 'cosmetics' ? 'active' : ''}`}
            onClick={() => setActiveTab('cosmetics')}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
            </svg>
            Customize Look
          </button>
          <button
            className={`rewards-tab ${activeTab === 'roadmap' ? 'active' : ''}`}
            onClick={() => setActiveTab('roadmap')}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
            </svg>
            Level Roadmap
          </button>
        </div>

        {/* Cosmetics Tab */}
        {activeTab === 'cosmetics' && (
          <div className="cosmetics-grid">
            {/* Preview Card */}
            <div className="preview-card-new">
              <div className="preview-header">
                <h3>Your Profile</h3>
                <button
                  className="save-cosmetics-btn"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <div className="btn-spinner"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                      </svg>
                      Save Changes
                    </>
                  )}
                </button>
              </div>

              <div className="preview-body">
                <div className={`preview-avatar-new frame-${activeCosmetics.frame || 'none'} effect-${activeCosmetics.effect || 'none'}`}>
                  <div className="avatar-inner">
                    {localStorage.getItem('display_name')?.charAt(0)?.toUpperCase() || 'A'}
                  </div>
                </div>
                <div className="preview-name">{localStorage.getItem('display_name') || 'Anonymous'}</div>
                <div className="preview-title-badge">{activeCosmetics.title || 'Newcomer'}</div>
                <div className="preview-level">Level {currentLevel}</div>
              </div>
            </div>

            {/* Frames */}
            <div className="cosmetic-card">
              <div className="cosmetic-card-header">
                <h3>Profile Frames</h3>
                <span className="cosmetic-count">{frameRewards.filter(f => f.value === null || isUnlocked('frame', f.value)).length}/{frameRewards.length}</span>
              </div>
              <div className="cosmetic-options-grid">
                {frameRewards.map((item) => {
                  const unlocked = item.value === null || isUnlocked('frame', item.value);
                  const selected = activeCosmetics.frame === item.value;
                  return (
                    <button
                      key={item.value || 'none'}
                      className={`cosmetic-option ${selected ? 'selected' : ''} ${!unlocked ? 'locked' : ''}`}
                      onClick={() => unlocked && setActiveCosmetics({ ...activeCosmetics, frame: item.value })}
                      disabled={!unlocked}
                    >
                      <div className={`cosmetic-preview-mini frame-${item.value || 'none'}`} style={{ borderColor: item.color }}>
                        <span>A</span>
                      </div>
                      <span className="cosmetic-option-name">{item.name}</span>
                      {!unlocked && <span className="unlock-req">Lv.{item.level}</span>}
                      {selected && <div className="selected-check"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg></div>}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Titles */}
            <div className="cosmetic-card">
              <div className="cosmetic-card-header">
                <h3>Titles</h3>
                <span className="cosmetic-count">{titleRewards.filter(t => isUnlocked('title', t.value)).length}/{titleRewards.length}</span>
              </div>
              <div className="cosmetic-options-list">
                {titleRewards.map((item) => {
                  const unlocked = isUnlocked('title', item.value);
                  const selected = activeCosmetics.title === item.value;
                  return (
                    <button
                      key={item.value}
                      className={`title-option ${selected ? 'selected' : ''} ${!unlocked ? 'locked' : ''}`}
                      onClick={() => unlocked && setActiveCosmetics({ ...activeCosmetics, title: item.value })}
                      disabled={!unlocked}
                    >
                      <span className="title-name">{item.name}</span>
                      {!unlocked && <span className="unlock-req">Lv.{item.level}</span>}
                      {selected && <div className="selected-indicator"></div>}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Themes */}
            <div className="cosmetic-card">
              <div className="cosmetic-card-header">
                <h3>Dashboard Themes</h3>
                <span className="cosmetic-count">{themeRewards.filter(t => t.value === null || isUnlocked('theme', t.value)).length}/{themeRewards.length}</span>
              </div>
              <div className="cosmetic-options-grid themes">
                {themeRewards.map((item) => {
                  const unlocked = item.value === null || isUnlocked('theme', item.value);
                  const selected = activeCosmetics.theme === item.value;
                  return (
                    <button
                      key={item.value || 'default'}
                      className={`cosmetic-option theme-option ${selected ? 'selected' : ''} ${!unlocked ? 'locked' : ''}`}
                      onClick={() => unlocked && setActiveCosmetics({ ...activeCosmetics, theme: item.value })}
                      disabled={!unlocked}
                    >
                      <div className="theme-preview" style={{ background: `linear-gradient(135deg, ${item.colors[0]}, ${item.colors[1]})` }}></div>
                      <span className="cosmetic-option-name">{item.name}</span>
                      {!unlocked && <span className="unlock-req">Lv.{item.level}</span>}
                      {selected && <div className="selected-check"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg></div>}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Effects */}
            <div className="cosmetic-card">
              <div className="cosmetic-card-header">
                <h3>Special Effects</h3>
                <span className="cosmetic-count">{effectRewards.filter(e => e.value === null || isUnlocked('effect', e.value)).length}/{effectRewards.length}</span>
              </div>
              <div className="cosmetic-options-grid">
                {effectRewards.map((item) => {
                  const unlocked = item.value === null || isUnlocked('effect', item.value);
                  const selected = activeCosmetics.effect === item.value;
                  return (
                    <button
                      key={item.value || 'none'}
                      className={`cosmetic-option ${selected ? 'selected' : ''} ${!unlocked ? 'locked' : ''}`}
                      onClick={() => unlocked && setActiveCosmetics({ ...activeCosmetics, effect: item.value })}
                      disabled={!unlocked}
                    >
                      <div className={`effect-preview effect-${item.value || 'none'}`}>
                        <span>✦</span>
                      </div>
                      <span className="cosmetic-option-name">{item.name}</span>
                      {!unlocked && <span className="unlock-req">Lv.{item.level}</span>}
                      {selected && <div className="selected-check"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg></div>}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Roadmap Tab */}
        {activeTab === 'roadmap' && (
          <div className="roadmap-new">
            <div className="roadmap-path">
              {[1, 3, 5, 8, 10, 12, 15, 18, 20, 25, 30, 35, 40, 45, 50].map((level, idx) => {
                const unlocked = currentLevel >= level;
                const isCurrent = currentLevel === level;
                const rewardInfo = allRewards[level];

                let rewardType = '';
                let rewardName = '';
                if (rewardInfo) {
                  if (rewardInfo.frame) { rewardType = 'frame'; rewardName = rewardInfo.frame + ' Frame'; }
                  if (rewardInfo.title) { rewardType = 'title'; rewardName = '"' + rewardInfo.title + '"'; }
                  if (rewardInfo.theme) { rewardType = 'theme'; rewardName = rewardInfo.theme + ' Theme'; }
                  if (rewardInfo.effect) { rewardType = 'effect'; rewardName = rewardInfo.effect + ' Effect'; }
                }

                return (
                  <div key={level} className={`roadmap-node ${unlocked ? 'unlocked' : ''} ${isCurrent ? 'current' : ''}`}>
                    <div className="roadmap-level-badge">
                      {unlocked ? (
                        <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                        </svg>
                      ) : (
                        <span>{level}</span>
                      )}
                    </div>
                    <div className="roadmap-content">
                      <span className="roadmap-level-text">Level {level}</span>
                      {rewardName && (
                        <span className={`roadmap-reward ${rewardType}`}>{rewardName}</span>
                      )}
                    </div>
                    {idx < 14 && <div className={`roadmap-connector ${unlocked ? 'unlocked' : ''}`}></div>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Eco Impact Card */}
        <div className="eco-impact-card">
          <div className="eco-impact-header">
            <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
              <path d="M12 22c4.97 0 9-4.03 9-9-4.97 0-9 4.03-9 9zM5.6 10.25c0 1.38 1.12 2.5 2.5 2.5.53 0 1.01-.16 1.42-.44l-.02.19c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5l-.02-.19c.4.28.89.44 1.42.44 1.38 0 2.5-1.12 2.5-2.5 0-1-.59-1.85-1.43-2.25.84-.4 1.43-1.25 1.43-2.25 0-1.38-1.12-2.5-2.5-2.5-.53 0-1.01.16-1.42.44l.02-.19C14.5 2.12 13.38 1 12 1S9.5 2.12 9.5 3.5l.02.19c-.4-.28-.89-.44-1.42-.44-1.38 0-2.5 1.12-2.5 2.5 0 1 .59 1.85 1.43 2.25-.84.4-1.43 1.25-1.43 2.25zM12 5.5c1.38 0 2.5 1.12 2.5 2.5s-1.12 2.5-2.5 2.5S9.5 9.38 9.5 8s1.12-2.5 2.5-2.5zM3 13c0 4.97 4.03 9 9 9 0-4.97-4.03-9-9-9z"/>
            </svg>
            <h3>Your Environmental Impact</h3>
          </div>
          <div className="eco-impact-stats">
            <div className="eco-impact-stat">
              <div className="eco-icon trees">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 22c4.97 0 9-4.03 9-9-4.97 0-9 4.03-9 9zM5.6 10.25c0 1.38 1.12 2.5 2.5 2.5.53 0 1.01-.16 1.42-.44l-.02.19c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5l-.02-.19c.4.28.89.44 1.42.44 1.38 0 2.5-1.12 2.5-2.5 0-1-.59-1.85-1.43-2.25.84-.4 1.43-1.25 1.43-2.25 0-1.38-1.12-2.5-2.5-2.5-.53 0-1.01.16-1.42.44l.02-.19C14.5 2.12 13.38 1 12 1S9.5 2.12 9.5 3.5l.02.19c-.4-.28-.89-.44-1.42-.44-1.38 0-2.5 1.12-2.5 2.5 0 1 .59 1.85 1.43 2.25-.84.4-1.43 1.25-1.43 2.25zM12 5.5c1.38 0 2.5 1.12 2.5 2.5s-1.12 2.5-2.5 2.5S9.5 9.38 9.5 8s1.12-2.5 2.5-2.5zM3 13c0 4.97 4.03 9 9 9 0-4.97-4.03-9-9-9z"/></svg>
              </div>
              <div className="eco-stat-info">
                <span className="eco-stat-value">{(co2Saved / 0.021).toFixed(0)}</span>
                <span className="eco-stat-label">Trees Equivalent</span>
              </div>
            </div>
            <div className="eco-impact-stat">
              <div className="eco-icon car">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/></svg>
              </div>
              <div className="eco-stat-info">
                <span className="eco-stat-value">{(co2Saved / 0.21).toFixed(1)}</span>
                <span className="eco-stat-label">km Car Offset</span>
              </div>
            </div>
            <div className="eco-impact-stat">
              <div className="eco-icon energy">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M11 21h-1l1-7H7.5c-.58 0-.57-.32-.38-.66.19-.34.05-.08.07-.12C8.48 10.94 10.42 7.54 13 3h1l-1 7h3.5c.49 0 .56.33.47.51l-.07.15C12.96 17.55 11 21 11 21z"/></svg>
              </div>
              <div className="eco-stat-info">
                <span className="eco-stat-value">{(coreHours * 0.0001).toFixed(2)}</span>
                <span className="eco-stat-label">kWh Energy Saved</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

export default Rewards;
